import { Colors } from "../../../../utils/colors";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import FormBuilderWrapper from "../../../../components/Form/FormBuilderWrapper";
import PreviewBoxContainer from "../../../../components/Containers/PreviewBoxContainer";
import StandardBorderedContainer from "../../../../components/Containers/StandardBorderedContainer";
import { useTheme } from "../../../../hooks/useTheme";
import RectangleButton from "../../../../components/Buttons/RectangleButton";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { onCampaignNavigatePath } from "../../../../utils/navigationUtils";
import { getCampaignState } from "../../../../redux/slices/campaignSlice";
import {
  massTextMessageConverter,
  SUBSCRIBED_CAMPAIGN_NAME,
} from "@markit/common.utils";
import RectangleButtonCreatorPanel from "../../../../components/Buttons/RectangleButtonCreatorPanel";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import ShareProfileLinkModal from "../../../../components/CreatorDashboard/CreatorProfile/ShareProfileLinkModal";
import { NavigationId } from "../../../../navigation/AppParamList";

const ProfileSubscribeFormPanel = () => {
  const { accountData } = useSelector(getAccountState).account;
  const { campaigns, campaignDetails } = useSelector(getCampaignState);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [shareModal, setShareModal] = useState(false);

  const styles = {
    headerText: { fontSize: 14, fontWeight: "500" },
    subtext: { fontSize: 12, color: Colors.GRAY1 },
    sectionContainer: {
      padding: 14,
      borderRadius: 12,
      backgroundColor: Colors.WHITE,
    },
  };

  const followCampaign = useMemo(
    () =>
      campaigns.find((campaign) => campaign.title === SUBSCRIBED_CAMPAIGN_NAME),
    [campaigns]
  );

  const followCampaignDetails = useMemo(
    () =>
      campaignDetails.find(
        (detail) => detail.campaignId === followCampaign?.id
      ),
    [campaignDetails, followCampaign]
  );

  const followMassText = useMemo(() => {
    if (followCampaignDetails) {
      return followCampaignDetails.massTextDetails[0].massText;
    }
    return undefined;
  }, [followCampaignDetails]);

  const convertedMessage = useMemo(() => {
    const massTextAttachments = followCampaignDetails
      ? followCampaignDetails.massTextDetails[0].attachments
      : [];
    return massTextMessageConverter(
      followMassText?.message ?? "",
      massTextAttachments,
      accountData.trustedSender
    );
  }, [
    followCampaignDetails,
    followMassText?.message,
    accountData.trustedSender,
  ]);

  const editFollowText = useCallback(() => {
    if (followCampaign) {
      navigate(onCampaignNavigatePath(followCampaign.id), {
        state: {
          campaignId: followCampaign.id,
          navigationPath: NavigationId.HOME_SUBSCRIBE_FORM,
        },
      });
    } else {
      alert("Unable to edit the follow text at this time.");
    }
  }, [followCampaign, navigate]);

  return (
    <CreatorModePanelWrapper
      title={"Profile Subscribe Form"}
      subtext="Customize your welcome flow. When people subscribe to your Markit profile, they will enter this flow."
      headerRight={
        <RectangleButtonCreatorPanel
          title="Share"
          iconName="ion:share"
          onPress={() => setShareModal(true)}
        />
      }
    >
      <div
        className="ColumnNormal"
        style={{ gap: 20, width: 544, paddingBlock: 30, marginInline: "auto" }}
      >
        <PreviewBoxContainer
          message={
            convertedMessage ??
            "Unable to display the follow text at this time."
          }
          containerStyles={{
            borderRadius: 12,
            backgroundColor: Colors.WHITE,
            border: `1.5px solid ${theme.DividerColor.borderColor}`,
          }}
          headerComp={
            <div className="AlignedRow" style={{ gap: 10 }}>
              <span className="sectionTitle">{SUBSCRIBED_CAMPAIGN_NAME}</span>
              <Icon
                icon="mdi:hand-wave"
                height={20}
                style={{ color: Colors.BLACK }}
              />
            </div>
          }
          subHeaderComp={
            <span style={{ fontSize: 12, color: Colors.GRAY1 }}>
              Sent when someone follows you
            </span>
          }
          headerRightComp={
            <RectangleButton
              buttonLabel="Edit"
              onPress={editFollowText}
              altBorderRadius={13}
              altPaddingHorz={12}
              altPaddingVert={12}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              iconRight={
                <Icon
                  icon={"ion:chevron-forward"}
                  height={16}
                  style={{ color: Colors.WHITE }}
                />
              }
            />
          }
        />
        <StandardBorderedContainer containerStyles={styles.sectionContainer}>
          <FormBuilderWrapper
            isProfileQuestions
            hostId={accountData.uid}
            disableAnimation
          />
        </StandardBorderedContainer>
        {shareModal ? (
          <ShareProfileLinkModal
            username={accountData.username}
            setShareLinkModal={setShareModal}
            isSubscribeLink
          />
        ) : null}
      </div>
    </CreatorModePanelWrapper>
  );
};

export default ProfileSubscribeFormPanel;
